import React from 'react';
import { Row, Col, Drawer, Typography, Space, Spin, Tag } from 'antd';
import { ChurnCloseSvgIcon, DashboardFilterIcon } from 'utils/icons';
import PropTypes from 'prop-types';
import DashBoardSummaryCard from 'components/DashBoardSummaryCard';
import CustomSkeleton from 'components/CustomSkeleton';
import PositiveNegativeBarChartGraph from 'components/PositiveNegativeBarChartGraph/PositiveNegativeBarChartGraph';
import CommissionLineChart from 'components/CommissionLineChart/CommissionLineChart';
import TopMerchantsBarChart from 'components/TopMerchantsBarChart';
import dayjs from 'dayjs';
import EbzButton from 'components/EbzButton';
import { saveAs } from 'file-saver';
import { DownloadOutlined } from '@ant-design/icons';
import DashBoardFilterPage from './DashboardFilter';
import DashboardCommomFooter from './DashboardCommomFooter';
import './DashBoardPage.scss';
import { COMMISSION_PAYMENT_MODE_COLUMNS_EXPORT, formatPaymentMode } from './const';

// This is the commission dashboard componentof the dashboard page
function CommissionPage({
  commissionPaymentModeLoading,
  topMerchantCommissionMonth,
  topMerchantCommissionLoading,
  commissionAggregateData,
  topMerchantCommissionData,
  commissionDateWise,
  filterCommissionGraphData,
  dashBoardForm,
  RANGE_PRESETS_DAYS,
  disabledDate,
  productList,
  merchants,
  hasSummaryData,
  summary,
  fetchMerchantList,
  merchantLoading,
  setMerchantPagination,
  resetDashboardForm,
  merchantTransactionListRef,
  selectedMerchantTotalCounts,
  handleOpen,
  openModal
}) {
  function convertToCSV(data) {
    const keys = Object.keys(COMMISSION_PAYMENT_MODE_COLUMNS_EXPORT);
    const header = Object.values(COMMISSION_PAYMENT_MODE_COLUMNS_EXPORT).join(',');

    const rows = data.map((obj) =>
      keys
        .map((key) => {
          const value = obj[key];
          if (key === 'mode') return formatPaymentMode(value);
          return value;
        })
        .join(',')
    );

    return `${header}\n${rows.join('\n')}`;
  }

  function handleCommissionExport() {
    const csvString = convertToCSV(commissionAggregateData || []);
    const csvData = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    saveAs(csvData, 'Commission_Payment_ModeWise.csv');
  }
  return (
    <>
      <Row gutter={[8, 8]} className="commission-page-row">
        <Col span={24}>
          <div className="commission-page">
            <Row
              align="middle"
              justify="space-between"
              style={{ gap: '14px', paddingTop: '9px', paddingBottom: '5px' }}
            >
              <Col>
                <Typography style={{ fontSize: '20px', fontWeight: '500' }}>
                  Commission Report
                </Typography>
              </Col>
              <Col>
                <EbzButton
                  icon={
                    <DashboardFilterIcon style={{ fontSize: 16, color: 'var(--color-primary)' }} />
                  }
                  size="large"
                  style={{
                    display: 'flex',
                    border: '1px solid',
                    alignItems: 'center',
                    gap: 2,
                    borderColor: 'var(--color-primary)',
                    color: 'var(--color-primary)',
                    borderRadius: 4,
                    padding: '0 16px',
                    fontWeight: 500
                  }}
                  onClick={() => handleOpen()}
                >
                  <Typography.Text style={{ color: 'var(--color-primary)' }}>
                    Filters
                  </Typography.Text>
                </EbzButton>
              </Col>
            </Row>
            <Row gutter={[18, 18]}>
              <Col span={24} style={{ paddingTop: '24px' }}>
                {hasSummaryData ? (
                  <DashBoardSummaryCard dashBoardSummary={summary} summaryKey="COMMISSION" />
                ) : (
                  <CustomSkeleton />
                )}
              </Col>
            </Row>
            <Row gutter={[8, 32]} style={{ marginTop: '4%' }}>
              <Typography style={{ fontSize: '20px', fontWeight: '500' }}>
                Commission Data
              </Typography>
              <Col span={24}>
                <Spin spinning={commissionPaymentModeLoading}>
                  <CommissionLineChart commissionDateWise={commissionDateWise} />
                </Spin>
              </Col>
              <Col span={24}>
                <Row
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Col>
                    <Typography style={{ fontSize: '20px', fontWeight: 500 }}>
                      Payment Mode Wise Data
                    </Typography>
                  </Col>
                  <Col>
                    <EbzButton
                      onClick={() => handleCommissionExport()}
                      disabled={!commissionAggregateData || commissionAggregateData.length === 0}
                      size="large"
                      type="default"
                      style={{
                        color: 'var(--color-primary)',
                        border: '1px solid',
                        borderRadius: 4
                      }}
                      icon={<DownloadOutlined />}
                    >
                      <Typography.Text style={{ fontSize: '14px', color: 'var(--color-primary)' }}>
                        Export
                      </Typography.Text>
                    </EbzButton>
                  </Col>
                </Row>
              </Col>

              <Col span={24}>
                <Spin spinning={commissionPaymentModeLoading}>
                  <PositiveNegativeBarChartGraph commissionData={commissionAggregateData} />
                </Spin>
              </Col>
            </Row>
          </div>
        </Col>
        <Col span={24} style={{ paddingLeft: '6px', paddingRight: '6px' }}>
          <Row gutter={[8, 16]} className="commission-top-merchants">
            <Space direction="horizontal" style={{ gap: '30px', display: 'flex' }}>
              <Space>
                <Typography style={{ fontSize: '20px', fontWeight: '500' }}>
                  Top 10 Merchants
                </Typography>
              </Space>
              {topMerchantCommissionMonth && (
                <Space>
                  <Tag color="purple">
                    For {dayjs(topMerchantCommissionMonth).format('MMMM YYYY')}
                  </Tag>
                </Space>
              )}
            </Space>
            <Col span={24}>
              <Spin spinning={topMerchantCommissionLoading}>
                <TopMerchantsBarChart
                  barChartData={topMerchantCommissionData}
                  XaxisKey="Merchant ID"
                  yAxislabel="gtv"
                  YaxisKey="Gross Commisison Value"
                />
              </Spin>
            </Col>
          </Row>
        </Col>
      </Row>
      <Drawer
        title={
          <Space style={{ justifyContent: 'space-between', display: 'flex' }}>
            <Space>
              <Typography.Text>Filters</Typography.Text>
            </Space>
            <Space>
              <ChurnCloseSvgIcon
                style={{ fontSize: '25px', color: 'var(--color-primary)' }}
                onClick={() => handleOpen()}
              />
            </Space>
          </Space>
        }
        placement="right"
        width={500}
        closable={false}
        open={openModal}
        onClose={handleOpen}
        rootClassName="transaction-drawer"
        footer={
          <DashboardCommomFooter
            filterGraphData={filterCommissionGraphData}
            handleOpen={handleOpen}
            resetDashboardForm={resetDashboardForm}
            dashBoardForm={dashBoardForm}
          />
        }
      >
        <DashBoardFilterPage
          formName="commissionFilterForm"
          productList={productList}
          merchants={merchants}
          filterGraphData={filterCommissionGraphData}
          dashBoardForm={dashBoardForm}
          RANGE_PRESETS_DAYS={RANGE_PRESETS_DAYS}
          disabledDate={disabledDate}
          fetchMerchantList={fetchMerchantList}
          merchantLoading={merchantLoading}
          setMerchantPagination={setMerchantPagination}
          merchantTransactionListRef={merchantTransactionListRef}
          selectedMerchantTotalCounts={selectedMerchantTotalCounts}
        />
      </Drawer>
    </>
  );
}

CommissionPage.propTypes = {
  commissionPaymentModeLoading: PropTypes.bool.isRequired,
  topMerchantCommissionLoading: PropTypes.bool.isRequired,
  topMerchantCommissionMonth: PropTypes.string.isRequired,
  topMerchantCommissionData: PropTypes.instanceOf(Array).isRequired,
  commissionAggregateData: PropTypes.instanceOf(Array).isRequired,
  commissionDateWise: PropTypes.instanceOf(Array).isRequired,
  filterCommissionGraphData: PropTypes.func.isRequired,
  dashBoardForm: PropTypes.instanceOf(Object).isRequired,
  RANGE_PRESETS_DAYS: PropTypes.instanceOf(Array).isRequired,
  disabledDate: PropTypes.func.isRequired,
  productList: PropTypes.instanceOf(Array).isRequired,
  merchants: PropTypes.instanceOf(Array).isRequired,
  hasSummaryData: PropTypes.bool.isRequired,
  summary: PropTypes.instanceOf(Object).isRequired,
  fetchMerchantList: PropTypes.func.isRequired,
  merchantLoading: PropTypes.bool.isRequired,
  setMerchantPagination: PropTypes.func.isRequired,
  resetDashboardForm: PropTypes.func.isRequired,
  merchantTransactionListRef: PropTypes.instanceOf(Object).isRequired,
  selectedMerchantTotalCounts: PropTypes.number.isRequired,
  handleOpen: PropTypes.func.isRequired,
  openModal: PropTypes.bool.isRequired
};
export default CommissionPage;
